import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import { createConnection } from "@/api/connections";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage } from "@/lib/api-errors";
import { ssoOneCProblem, useOneCBases } from "@/lib/use-onec-bases";

/** Новое подключение в SSO-сессии: база из справочника, без логина и пароля.
 *  В 1С портал входит токеном SSO от имени пользователя 1С, заданного в
 *  учётной записи SSO (`onec_unf`); адрес публикации берётся из справочника. */
export function SsoConnectionCard(): React.ReactElement {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useOneCBases();
  const [name, setName] = useState("");
  const [baseId, setBaseId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: createConnection,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["connections"] });
      setName("");
      setBaseId("");
      setFormError(null);
      toast.success("Подключение создано");
    },
    onError: (e: unknown) => {
      const msg = extractErrorMessage(e, "Ошибка создания");
      setFormError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const missing = [!name.trim() && "Название", !baseId && "База 1С"].filter(Boolean);
    if (missing.length > 0) {
      setFormError(`Заполните обязательные поля: ${missing.join(", ")}`);
      return;
    }
    setFormError(null);
    createMut.mutate({ name: name.trim(), onec_base_id: baseId });
  };

  const onPickBase = (id: string) => {
    setBaseId(id);
    // Название по умолчанию — имя базы, если человек его ещё не вводил.
    const picked = data?.bases.find((b) => b.id === id);
    if (picked && !name.trim()) setName(picked.name);
  };

  const problem = data ? ssoOneCProblem(data.has_onec_unf) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Новое подключение</CardTitle>
        <p className="text-sm text-muted-foreground">
          Вы вошли через SSO: в 1С портал входит токеном SSO от имени пользователя 1С, заданного в
          вашей учётной записи SSO. Логин и пароль 1С не нужны — выберите базу из справочника.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка справочника баз…</p>}
        {error && (
          <div className="flex items-center gap-3 text-sm text-destructive">
            Справочник баз 1С не загрузился: {extractErrorMessage(error, "нет ответа")}
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Повторить
            </Button>
          </div>
        )}
        {problem && (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div>
              <div className="font-semibold">Не настроен доступ к 1С</div>
              <div>{problem}</div>
            </div>
          </div>
        )}
        {data && data.bases.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Справочник баз 1С пуст — обратитесь к администратору портала.
          </p>
        )}
        {data && data.bases.length > 0 && (
          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <div className="space-y-2">
              <Label htmlFor="sso-conn-base">База 1С *</Label>
              <select
                id="sso-conn-base"
                value={baseId}
                onChange={(e) => onPickBase(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">— выберите базу —</option>
                {data.bases.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.base_url})
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sso-conn-name">Название *</Label>
              <Input
                id="sso-conn-name"
                placeholder="Основная база УНФ"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            {formError && (
              <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {formError}
              </p>
            )}
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending && <Loader2 className="size-4 animate-spin" />}
              Создать
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
